/**
 * Kafka Metrics Card Component
 * Displays Kafka topic message counts and daily trends
 */

import { useQuery } from '@tanstack/react-query'
import { Activity, Calendar, RefreshCw } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { LoadingState, ErrorState } from '@/components/ui/loading-state'
import { getKafkaTopicsMetrics, type KafkaTopicsMetricsResponse, type KafkaTopicMetrics } from '../queries/executions'

interface KafkaMetricsCardProps {
  topics?: string[]
  days?: number
  title?: string
  className?: string
}

export function KafkaMetricsCard({
  topics = ['custom-raw', 'datalist-raw'],
  days = 7,
  title = 'Event Ingestion',
  className = '',
}: KafkaMetricsCardProps) {
  const {
    data: metrics,
    isLoading,
    error,
    refetch,
  } = useQuery<KafkaTopicsMetricsResponse>({
    queryKey: ['kafka-metrics', topics, days],
    queryFn: () => getKafkaTopicsMetrics(topics, days),
    refetchInterval: 60000, // Refresh every minute
    retry: 2,
  })

  if (isLoading) {
    return (
      <Card className={className}>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Activity className="h-5 w-5 text-purple-500" />
            {title}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <LoadingState message="Loading Kafka metrics..." />
        </CardContent>
      </Card>
    )
  }

  if (error) {
    return (
      <Card className={className}>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Activity className="h-5 w-5 text-purple-500" />
            {title}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <ErrorState
            error={error as Error}
            message="Failed to load Kafka metrics"
            onRetry={() => refetch()}
          />
        </CardContent>
      </Card>
    )
  }

  return (
    <Card className={className}>
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <Activity className="h-5 w-5 text-purple-500" />
            {title}
          </CardTitle>
          <Button variant="ghost" size="sm" onClick={() => refetch()}>
            <RefreshCw className="h-4 w-4" />
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {/* Summary Stats */}
        <div className="grid grid-cols-2 gap-4 mb-4">
          <div className="bg-purple-50 rounded-lg p-3">
            <div className="text-xs text-purple-600 font-medium">Today's Events</div>
            <div className="text-2xl font-bold text-purple-700">
              {formatNumber(metrics?.todayMessages || 0)}
            </div>
          </div>
          <div className="bg-blue-50 rounded-lg p-3">
            <div className="text-xs text-blue-600 font-medium">Total Events</div>
            <div className="text-2xl font-bold text-blue-700">
              {formatNumber(metrics?.totalMessages || 0)}
            </div>
          </div>
        </div>

        {/* Per-Topic Breakdown */}
        <div className="space-y-3">
          <div className="text-sm font-medium text-gray-700">By Topic</div>
          {metrics?.topics.map((topic) => (
            <TopicRow key={topic.topic} topic={topic} />
          ))}
        </div>

        {/* Last 7 Days Chart (simplified bar chart) */}
        {metrics?.topics[0]?.dailyCounts && (
          <div className="mt-4 pt-4 border-t">
            <div className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
              <Calendar className="h-4 w-4" />
              Last {days} Days
            </div>
            <DailyTrendChart
              dailyCounts={aggregateDailyCounts(metrics.topics)}
            />
          </div>
        )}
      </CardContent>
    </Card>
  )
}

/**
 * Topic row showing individual topic stats
 */
function TopicRow({ topic }: { topic: KafkaTopicMetrics }) {
  const topicLabel = topic.topic
    .replace(/-raw$/, '')
    .replace(/-/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase())

  return (
    <div className="flex items-center justify-between bg-gray-50 rounded-lg px-3 py-2">
      <div className="flex items-center gap-2">
        <div className="w-2 h-2 rounded-full bg-green-500" />
        <span className="text-sm font-medium text-gray-700">{topicLabel}</span>
        <span className="text-xs text-gray-400">({topic.topic})</span>
      </div>
      <div className="flex items-center gap-4">
        <div className="text-right">
          <div className="text-sm font-semibold text-gray-900">
            {formatNumber(topic.todayMessages)}
          </div>
          <div className="text-xs text-gray-500">today</div>
        </div>
        <div className="text-right">
          <div className="text-sm font-semibold text-gray-600">
            {formatNumber(topic.totalMessages)}
          </div>
          <div className="text-xs text-gray-500">total</div>
        </div>
      </div>
    </div>
  )
}

/**
 * Simple bar chart for daily trends
 */
function DailyTrendChart({ dailyCounts }: { dailyCounts: { date: string; count: number }[] }) {
  if (!dailyCounts || dailyCounts.length === 0) {
    return <div className="text-sm text-gray-400">No data available</div>
  }

  const maxCount = Math.max(...dailyCounts.map((d) => d.count), 1)

  return (
    <div className="flex items-end gap-1 h-16">
      {dailyCounts.map((day, index) => {
        const height = (day.count / maxCount) * 100
        const isToday = index === 0

        return (
          <div
            key={day.date}
            className="flex-1 flex flex-col items-center gap-1"
            title={`${formatDate(day.date)}: ${formatNumber(day.count)} events`}
          >
            <div
              className={`w-full rounded-t transition-all ${
                isToday ? 'bg-purple-500' : 'bg-purple-200'
              }`}
              style={{ height: `${Math.max(height, 4)}%` }}
            />
            <span className="text-[10px] text-gray-400">
              {new Date(day.date).getDate()}
            </span>
          </div>
        )
      })}
    </div>
  )
}

// Helper functions

function formatNumber(num: number): string {
  if (num >= 1000000) {
    return `${(num / 1000000).toFixed(1)}M`
  }
  if (num >= 1000) {
    return `${(num / 1000).toFixed(1)}K`
  }
  return num.toLocaleString()
}

function formatDate(dateStr: string): string {
  const date = new Date(dateStr)
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}

/**
 * Aggregate daily counts from multiple topics
 */
function aggregateDailyCounts(
  topics: KafkaTopicMetrics[]
): { date: string; count: number }[] {
  const aggregated: Record<string, number> = {}

  for (const topic of topics) {
    for (const day of topic.dailyCounts || []) {
      aggregated[day.date] = (aggregated[day.date] || 0) + day.count
    }
  }

  // Sort by date descending (most recent first)
  return Object.entries(aggregated)
    .sort(([a], [b]) => b.localeCompare(a))
    .map(([date, count]) => ({ date, count }))
}

export default KafkaMetricsCard
